import Vue from 'vue';
import Router from 'vue-router';

import App from './App.vue';
import AppLayout from './AppLayout.vue';
import AppAgentLayout from './AppAgentLayout.vue';

import { requireAuth } from '@/utils/auth';
import { SettingsResponseModel } from '@/api/settings';

import { IsAuthorizedType } from './components/_global/permissions/store';

Vue.use(Router);

const PageNotFound = Vue.component("page-not-found", {
	template: "",
	created: function () {
		window.location.href = "/";
	}
});

import ErrorPage from "@/components/_global/ErrorPage.vue";


export default new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [
		{
			path: '/', component: App,
			children: [
				{
					path: '/',
					component: AppLayout,
					beforeEnter: requireAuth('USER'),
					children: [
						{
							path: '/',
							component: () => import('./components/dashboard/DashboardPage.vue'),
							meta: {
								title: 'menu.dashboard'
							}
						},
						{
							path: '/profile',
							component: () => import('./components/profile/UserProfilePage.vue'),
							meta: {
								title: 'menu.profile'
							}
						},
						{
							path: '/accounts/balance',
							component: () => import('./components/account/AccountsListPage.vue'),
							meta: {
								title: 'menu.balance',
								enabled: (settings: SettingsResponseModel) => settings.payment.enableAccountsConsultation
							}
						},
						{
							path: '/accounts/transactions',
							component: () => import('./components/account/TransactionsListPage.vue'),
							meta: {
								title: 'menu.transactions',
								enabled: (settings: SettingsResponseModel) => settings.payment.enableAccountsConsultation
							}
						},
						{
							path: '/history',
							component: () => import('./components/event/EventsListPage.vue'),
							meta: {
								title: 'menu.history_bill',
								enabled: (settings: SettingsResponseModel) => settings.payment.enableAccountsConsultation
							}
						},
						{
							path: '/booking',
							component: () => import('./components/booking/BookingPage.vue'),
							meta: {
								title: 'menu.booking_time',
								enabled: (settings: SettingsResponseModel) => settings.booking.enableBooking
							}
						},
						{
							path: '/booking_resources',
							component: () => import('./components/booking-resources/BookingResourcesPage.vue'),
							meta: {
								title: 'menu.booking_resources',
								enabled: (settings: SettingsResponseModel) => settings.bookingResources.enable
							}
						},
						{
							path: '/booking_shop',
							component: () => import('./components/booking-shop/BookingShopPage.vue'),
							meta: {
								title: 'menu.booking_shop',
								enabled: (settings: SettingsResponseModel) => settings.bookingShop.enable
							}
						},
						{
							path: '/booking_shop/:id',
							component: () => import('./components/booking-shop/BookingShopDetailsPage.vue'),
							meta: {
								title: 'menu.booking_shop',
								enabled: (settings: SettingsResponseModel) => settings.bookingShop.enable
							}
						},
						{
							path: '/payment/confirm',
							component: () => import('./components/account/PaymentConfirmPage.vue'),
							meta: {
								enabled: (settings: SettingsResponseModel) => settings.payment.enablePayment
							}
						},
						{
							path: '/payment/failed',
							component: () => import('./components/account/PaymentFailedPage.vue'),
							meta: {
								enabled: (settings: SettingsResponseModel) => settings.payment.enablePayment
							}
						},

					]
				},
				{
					path: '/admin',
					component: AppAgentLayout,
					beforeEnter: requireAuth('AGENT'),
					children: [
						{
							path: '/admin',
							component: () => import('./components/admin/home/AgentHomePage.vue'),
							meta: {

							}
						},
						{
							path: '/admin/settings/basic',
							component: () => import('./components/admin/settings/SettingsPage.vue'),
							meta: {
								title: 'menu.settings_basic',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canEditBasicParameters)
							}
						},
						{
							path: '/admin/settings/customization',
							component: () => import('./components/admin/settings/CustomizationSettingsPage.vue'),
							meta: {
								title: 'menu.settings_customization',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canEditCustomizationParameters)
							}
						},
						{
							path: '/admin/settings/advanced',
							component: () => import('./components/admin/settings/AdvancedSettingsPage.vue'),
							meta: {
								title: 'menu.settings_advanced',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canEditAdvancedParameters)
							}
						},
						{
							path: '/admin/settings/features',
							component: () => import('./components/admin/settings/FeaturesPage.vue'),
							meta: {
								title: 'menu.settings_features',
								isAuthorized: (f: IsAuthorizedType) => f((x) => false)
							}
						},
						{
							path: '/admin/logs',
							component: () => import('./components/admin/logs/LogsListPage.vue'),
							meta: {
								title: 'menu.logs',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canViewApplicationLogs)
							}
						},
						{
							path: '/admin/logs/synchro',
							component: () => import('./components/admin/logs/SynchroLogsListPage.vue'),
							meta: {
								title: 'menu.logs_synchro',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canViewSynchronizationLogs)
							}
						},
						{
							path: '/admin/synchronization',
							component: () => import('./components/admin/synchronization/SynchronizationHistoryPage.vue'),
							meta: {
								title: 'menu.synchronization',
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canViewSynchronizationHistory)
							}
						},
						{
							path: '/admin/bookings/',
							component: () => import('./components/admin/booking/BookingsListPage.vue'),
							meta: {
								title: 'menu.bookings_list',
								enabled: (settings: SettingsResponseModel) => settings.booking.enableBooking,
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canBookForUsers)

							}
						},
						{
							path: '/admin/time_ranges/editor',
							component: () => import('./components/admin/booking/TimeRangesEditorPage.vue'),
							meta: {
								title: 'menu.time_ranges_editor',
								enabled: (settings: SettingsResponseModel) => settings.bookingResources.enable,
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canEditTimeRanges)
							}
						},
						{
							path: '/admin/booking_resources/editor',
							component: () => import('./components/admin/booking/BookingResourcesPage.vue'),
							meta: {
								title: 'menu.booking_editor',
								enabled: (settings: SettingsResponseModel) => settings.bookingResources.enable,
								isAuthorized: (f: IsAuthorizedType) => f((x) => x.canBookForUsers)
							}
						}
					]
				},
				{
					path: '/login',
					name: 'login',
					component: () => import('./components/authentication/LoginPage.vue'),
				},
				{
					path: '/admin/login',
					name: 'agent-login',
					component: () => import('./components/authentication/AgentLoginPage.vue'),
				},
				{
					path: '/reset',
					name: 'reset',
					component: () => import('./components/authentication/ResetPasswordPage.vue'),
					meta: {
						enabled: (settings: SettingsResponseModel) => settings.general.enableForgotPassword
					}
				},
				{
					path: '/activate',
					name: 'activate',
					component: () => import('./components/authentication/PrepareActivateAccountPage.vue'),
					meta: {
						enabled: (settings: SettingsResponseModel) => settings.general.enableActivateAccount
					}
				},
				{
					path: '/activate/confirm',
					name: 'confirm_activate',
					component: () => import('./components/authentication/ActivateAccountPage.vue'),
					meta: {
						enabled: (settings: SettingsResponseModel) => settings.general.enableActivateAccount
					}
				},

			],

		},
		{ path: "/error", component: ErrorPage },
		{ path: "*", component: PageNotFound }
	],
});
